import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowRight, Target, TrendingUp, BarChart3, Cog, Database, Megaphone } from "lucide-react";
import axios from "axios";
import AccountCapture from "@/components/AccountCapture";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/components/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pillars = [
  { name: "Strategic Direction", icon: Target, color: "#DC0706" },
  { name: "Marketing System", icon: Megaphone, color: "#FE4D00" },
  { name: "Sales System", icon: TrendingUp, color: "#FF7F00" },
  { name: "Operations System", icon: Cog, color: "#FDB714" },
  { name: "Systems & Automation", icon: Database, color: "#02808F" },
  { name: "Data & Reporting", icon: BarChart3, color: "#9CCEFF" },
];

export default function LandingPage() {
  const [showCapture, setShowCapture] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { logos, logoStyle } = useTheme();

  const handleAccountSubmit = async (formData) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/audits`, formData);
      navigate(`/audit/${res.data.audit_id}`);
    } catch (err) {
      console.error("Failed to create audit:", err);
      setLoading(false);
    }
  };

  if (showCapture) {
    return (
      <AccountCapture
        onSubmit={handleAccountSubmit}
        onBack={() => setShowCapture(false)}
        loading={loading}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rhz-bg)', color: 'var(--rhz-text-primary)' }} data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ backgroundColor: 'var(--rhz-nav-bg)', borderBottom: '1px solid var(--rhz-nav-border)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <img src={logos.horizontal} alt="RedHotZone" className="h-7 sm:h-10" data-testid="logo" style={logoStyle} />
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Button
              data-testid="nav-start-audit-btn"
              onClick={() => setShowCapture(true)}
              className="bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-colors px-4 sm:px-6 py-2 uppercase tracking-[0.2em] font-['Bruno_Ace_SC'] text-[10px] sm:text-xs rounded-sm"
            >
              <span className="hidden sm:inline">Start Audit</span>
              <span className="sm:hidden">Start</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#DC0706]/5 via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-left"
          >
            <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase mb-6" style={{ color: 'var(--rhz-text-secondary)' }} data-testid="hero-subtitle">
              Business Diagnostic Tool
            </p>
            <h1
              className="font-['Bruno_Ace_SC'] text-4xl sm:text-5xl lg:text-6xl tracking-[0.15em] leading-[1.1] uppercase mb-8"
              data-testid="hero-title"
            >
              <span style={{ color: 'var(--rhz-text-primary)' }}>RHZ</span>{" "}
              <span className="text-flame">Growth Audit</span>
            </h1>
            <p className="font-['Albert_Sans'] text-lg sm:text-xl max-w-2xl leading-relaxed mb-12" style={{ color: 'var(--rhz-text-secondary)' }}>
              A structured diagnostic that identifies the real problems holding your business back.
              No fluff. No sales pitch. Just a clear, analytical assessment of where you stand.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-start-audit-btn"
                onClick={() => setShowCapture(true)}
                className="bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-all px-10 py-6 uppercase tracking-[0.25em] font-['Bruno_Ace_SC'] text-sm rounded-sm glow-red"
              >
                Start Your Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="hero-learn-more-btn"
                variant="ghost"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="uppercase tracking-[0.25em] font-['Bruno_Ace_SC'] text-sm px-10 py-6"
                style={{ color: 'var(--rhz-text-secondary)' }}
              >
                How It Works
              </Button>
            </div>
          </motion.div>

          {/* Stats strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-12 sm:mt-20 grid grid-cols-3 gap-3 sm:gap-6"
          >
            {[
              { value: "30", label: "Diagnostic Questions" },
              { value: "6", label: "Business Pillars" },
              { value: "5–7", label: "Minutes to Complete" },
            ].map((stat, i) => (
              <div key={i} className="pl-3 sm:pl-6" style={{ borderLeft: '2px solid var(--rhz-border)' }}>
                <p className="font-['Bruno_Ace_SC'] text-xl sm:text-3xl" style={{ color: 'var(--rhz-text-primary)' }} data-testid={`stat-value-${i}`}>{stat.value}</p>
                <p className="font-['Albert_Sans'] text-[10px] sm:text-sm uppercase tracking-wider mt-1" style={{ color: 'var(--rhz-text-secondary)' }}>{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6" style={{ borderTop: '1px solid var(--rhz-border)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Process</p>
          <h2 className="font-['Bruno_Ace_SC'] text-2xl sm:text-3xl tracking-[0.15em] uppercase mb-16" style={{ color: 'var(--rhz-text-primary)' }} data-testid="how-it-works-title">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Answer Questions", desc: "Complete a focused 30-question assessment covering 6 core business pillars. One question at a time, around 5–7 minutes." },
              { step: "02", title: "Instant Analysis", desc: "Your responses are scored and analysed against industry benchmarks to identify patterns and weaknesses." },
              { step: "03", title: "Diagnostic Report", desc: "Receive a structured report highlighting what's wrong, why it matters, and what successful businesses do differently." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="rounded-sm p-8 transition-colors"
                style={{ backgroundColor: 'var(--rhz-surface)', border: '1px solid var(--rhz-border)', boxShadow: 'var(--rhz-card-shadow)' }}
                data-testid={`step-card-${i}`}
              >
                <span className="font-['Bruno_Ace_SC'] text-3xl text-flame">{item.step}</span>
                <h3 className="font-['Bruno_Ace_SC'] text-sm uppercase tracking-[0.2em] mt-6 mb-3" style={{ color: 'var(--rhz-text-primary)' }}>{item.title}</h3>
                <p className="font-['Albert_Sans'] text-sm leading-relaxed" style={{ color: 'var(--rhz-text-secondary)' }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ borderTop: '1px solid var(--rhz-border)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Framework</p>
          <h2 className="font-['Bruno_Ace_SC'] text-2xl sm:text-3xl tracking-[0.15em] uppercase mb-16" style={{ color: 'var(--rhz-text-primary)' }} data-testid="pillars-title">
            6 Growth Pillars
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-4 p-5 rounded-sm transition-colors"
                  style={{ backgroundColor: 'var(--rhz-surface)', border: '1px solid var(--rhz-border)', boxShadow: 'var(--rhz-card-shadow)' }}
                  data-testid={`pillar-card-${i}`}
                >
                  <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${p.color}15` }}>
                    <Icon className="h-5 w-5" style={{ color: p.color }} />
                  </div>
                  <span className="font-['Albert_Sans'] font-semibold text-sm uppercase tracking-wider" style={{ color: 'var(--rhz-text-primary)' }}>{p.name}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ borderTop: '1px solid var(--rhz-border)' }}>
        <div className="max-w-5xl mx-auto">
          <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Output</p>
          <h2 className="font-['Bruno_Ace_SC'] text-2xl sm:text-3xl tracking-[0.15em] uppercase mb-6" style={{ color: 'var(--rhz-text-primary)' }} data-testid="output-title">
            What You Get
          </h2>
          <p className="font-['Albert_Sans'] text-base mb-16 max-w-2xl" style={{ color: 'var(--rhz-text-secondary)' }}>
            A diagnostic report that tells you what's wrong and why it matters. This is not a sales pitch — it's an honest assessment.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Executive Summary", desc: "A clear overview of your business maturity level and the most critical weaknesses identified." },
              { title: "Pillar Scorecard", desc: "Each of the 6 pillars scored on a 0–10 scale and rated: Poor, Developing, Structured, Advanced, or Optimised." },
              { title: "Key Findings", desc: "For each issue: what's happening, why it's a problem, and what high-performing businesses typically do." },
              { title: "Growth Constraints", desc: "The top 3-5 issues that are most likely limiting your business growth, ranked by impact." },
            ].map((item, i) => (
              <div
                key={i}
                className="p-6 border-l-4 border-l-[#DC0706] rounded-sm"
                style={{ backgroundColor: 'var(--rhz-surface)', boxShadow: 'var(--rhz-card-shadow)' }}
                data-testid={`output-card-${i}`}
              >
                <h3 className="font-['Bruno_Ace_SC'] text-sm uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--rhz-text-primary)' }}>{item.title}</h3>
                <p className="font-['Albert_Sans'] text-sm leading-relaxed" style={{ color: 'var(--rhz-text-secondary)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6" style={{ borderTop: '1px solid var(--rhz-border)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-['Bruno_Ace_SC'] text-2xl sm:text-3xl tracking-[0.15em] uppercase mb-6" style={{ color: 'var(--rhz-text-primary)' }} data-testid="cta-title">
            Ready to Audit Your Business?
          </h2>
          <p className="font-['Albert_Sans'] text-base mb-10" style={{ color: 'var(--rhz-text-secondary)' }}>
            30 questions. 5–7 minutes. Instant diagnostic report.
          </p>
          <Button
            data-testid="cta-start-audit-btn"
            onClick={() => setShowCapture(true)}
            className="bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-all px-12 py-6 uppercase tracking-[0.25em] font-['Bruno_Ace_SC'] text-sm rounded-sm glow-red"
          >
            Start Your Audit Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6" style={{ borderTop: '1px solid var(--rhz-border)' }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <img src={logos.horizontal} alt="RedHotZone" className="h-8 opacity-50" style={logoStyle} />
          <p className="font-['Albert_Sans'] text-xs" style={{ color: 'var(--rhz-text-disabled)' }}>Red Hot Zone Growth Audit</p>
        </div>
      </footer>
    </div>
  );
}
